// ...existing code...

/**
 * Returns the displayed products for the shop page, applying nav and advanced filters, grouping, and shuffling.
 * @param {Array} products - The list of all products
 * @param {string} filter - The current top nav filter (category)
 * @param {URLSearchParams} searchParams - The current URL search params
 * @returns {Array} - The filtered, grouped, and shuffled products
 */

// Canonical filter labels
export const FILTERS = ["All", "T-Shirts", "Jumpers", "Hats"];

// URL section to canonical filter mapping
export const PARAM_TO_FILTER = {
  tshirt: "T-Shirts",
  tshirts: "T-Shirts",
  jumpers: "Jumpers",
  hats: "Hats",
};

export const normalizeCategory = (value) => {
  if (!value) return "All";
  const c = String(value).toLowerCase();
  if (PARAM_TO_FILTER[c]) return PARAM_TO_FILTER[c];
  if (["t-shirts", "tshirt", "tshirts", "tees", "tee"].includes(c))
    return "T-Shirts";
  if (["jumpers", "hoodies", "sweatshirts"].includes(c)) return "Jumpers";
  if (["hats", "hat"].includes(c)) return "Hats";
  return "All";
};

// Parse filters from URLSearchParams
export const parseFilters = (searchParams) => {
  const section = searchParams.get("section");
  const mappedSection = section
    ? PARAM_TO_FILTER[String(section).toLowerCase()]
    : null;
  const categoriesStr = searchParams.get("categories") || "";
  const stylesStr = searchParams.get("styles") || "";
  const sizesStr = searchParams.get("sizes") || "";

  const categoriesRaw = categoriesStr
    ? categoriesStr.split(",").filter(Boolean)
    : mappedSection
      ? [mappedSection]
      : ["All"];

  return {
    categories: categoriesRaw.map(normalizeCategory),
    styles: stylesStr ? stylesStr.split(",").filter(Boolean) : ["All"],
    sizes: sizesStr ? sizesStr.split(",").filter(Boolean) : ["All"],
  };
};

// Serialize filters to URLSearchParams
export const serializeFilters = ({
  categories = [],
  styles = [],
  sizes = [],
}) => {
  const params = new URLSearchParams();
  const cats = categories.length ? categories : ["All"];
  // If a single non-All category is chosen, reflect it in section
  if (cats.length === 1 && cats[0] !== "All") {
    const reverse = { "T-Shirts": "tshirts", Jumpers: "jumpers", Hats: "hats" };
    params.set("section", reverse[cats[0]] || "");
  }
  params.set("categories", cats.join(","));
  if (styles.length) params.set("styles", styles.join(","));
  if (sizes.length) params.set("sizes", sizes.join(","));
  return params;
};

export function getDisplayedProducts(products, filter, searchParams) {
  // 1. Filter by top nav filter (category quick toggle)
  const byNav =
    filter === "All"
      ? products
      : products.filter(
          (product) => normalizeCategory(product.category) === filter,
        );

  // 2. Filter by advanced filters from menu or URL
  // Parse URL params first (so refresh keeps filters)
  const parsed = parseFilters(searchParams);
  const effCategories = parsed.categories;
  const effStyles = parsed.styles;
  const effSizes = parsed.sizes;

  const hasAll = (arr) => Array.isArray(arr) && arr.includes("All");

  const byAdvanced = byNav.filter((p) => {
    // Category match
    const catOk =
      hasAll(effCategories) ||
      effCategories.length === 0 ||
      effCategories.includes(normalizeCategory(p.category));
    // Style: use product.collection if available
    const styleOk =
      hasAll(effStyles) ||
      effStyles.length === 0 ||
      (p.collection && effStyles.includes(p.collection));
    // Size: intersect with product.sizes
    const sizeOk =
      hasAll(effSizes) ||
      effSizes.length === 0 ||
      (Array.isArray(p.sizes) && p.sizes.some((s) => effSizes.includes(s)));
    return catOk && styleOk && sizeOk;
  });

  // 3. Group by normalized title (remove Tee/Hoodie/hat) so variants stay together
  const normalizeTitleForGrouping = (title) => {
    if (!title) return "";
    return title
      .replace(/\b(tee|hoodie|jumper|hat|beanie|dark|light|plain)s?\b/gi, " ")
      .replace(/[^a-z0-9]+/gi, " ")
      .trim()
      .toLowerCase();
  };

  const groupsMap = new Map();
  for (const p of byAdvanced) {
    const key = normalizeTitleForGrouping(p.title || "");
    const arr = groupsMap.get(key) || [];
    arr.push(p);
    groupsMap.set(key, arr);
  }
  const groups = Array.from(groupsMap.values());

  /*// 4. Shuffle groups (Fisher–Yates)
  for (let i = groups.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [groups[i], groups[j]] = [groups[j], groups[i]];
  }*/

  // 5. Flatten back to a list, preserving order within groups
  return groups.flat().reverse(); // Reverse to show newest first (based on how backend returns)
}
